import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, MapPin, Calendar, Stars } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import './MyBandsPage.css';

/**
 * Every band belonging to the signed-in account.
 *
 * Ownership lives in Neon on zenith_runs.user_id, so this follows the account
 * rather than the browser — the whole point of having sign-in at all.
 *
 * Signed-out visitors are not bounced away. They get an explanation and a way
 * in, because being thrown to a login wall for a page you arrived at by
 * curiosity is a poor welcome.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleTimeString(undefined, {
    hour: '2-digit', minute: '2-digit',
  });
};

const coords = (lat, lon) =>
  lat == null || lon == null
    ? null
    : `${Number(lat).toFixed(3)}°, ${Number(lon).toFixed(3)}°`;

export default function MyBandsPage() {
  const { enabled, user, token } = useAuth();
  const navigate = useNavigate();
  const [bands, setBands] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { document.title = 'Your bands — Stars at Birth'; }, []);

  const load = useCallback(async () => {
    if (!token) { setBands(null); return; }
    try {
      const { data } = await axios.get(`${API}/auth/my-bands`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBands(data.bands || []);
      setError(null);
    } catch {
      setError('Could not load your bands.');
      setBands([]);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // --- signed out ---------------------------------------------------------
  if (!user) {
    return (
      <div className="mb">
        <Link className="mb-back" to="/">&larr; Stars at Birth</Link>
        <h1 className="mb-title">Your bands</h1>
        <p className="mb-lede">
          {enabled
            ? 'Sign in and every band you calculate is kept here, on any device.'
            : 'Accounts are not switched on yet.'}
        </p>
        <div className="mb-empty">
          <p>
            You do not need an account to calculate a band, or to buy one — this
            only keeps them together so you can find them again.
          </p>
          <Link className="mb-cta" to="/app">Calculate a band</Link>
        </div>
      </div>
    );
  }

  // --- loading ------------------------------------------------------------
  if (bands === null) {
    return (
      <div className="mb">
        <Link className="mb-back" to="/">&larr; Stars at Birth</Link>
        <h1 className="mb-title">Your bands</h1>
        <p className="mb-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading&hellip;</p>
      </div>
    );
  }

  return (
    <div className="mb">
      <Link className="mb-back" to="/">&larr; Stars at Birth</Link>

      <header className="mb-head">
        <h1 className="mb-title">Your bands</h1>
        <p className="mb-lede">
          {bands.length === 0
            ? 'Nothing here yet.'
            : `${bands.length} ${bands.length === 1 ? 'band' : 'bands'} on this account.`}
          {user.is_admin && <span className="mb-admin">admin</span>}
        </p>
      </header>

      {error && <p className="mb-error">{error}</p>}

      {bands.length === 0 ? (
        <div className="mb-empty">
          <p>Calculate one and it will appear here automatically.</p>
          <Link className="mb-cta" to="/app">Calculate a band</Link>
        </div>
      ) : (
        <ul className="mb-list">
          {bands.map((b) => (
            <li key={b.band_id}>
              <button
                className="mb-card"
                onClick={() => navigate(`/app?band=${b.band_id}`)}
                data-testid={`band-${b.band_id}`}
              >
                <span className="mb-card-name">{b.run_name || 'Untitled band'}</span>

                <span className="mb-card-meta">
                  <span><Calendar className="w-3 h-3" /> {fmtDate(b.birth_datetime)} {fmtTime(b.birth_datetime)}</span>
                  {coords(b.latitude, b.longitude) && (
                    <span><MapPin className="w-3 h-3" /> {coords(b.latitude, b.longitude)}</span>
                  )}
                  {b.object_count != null && (
                    <span><Stars className="w-3 h-3" /> {Number(b.object_count).toLocaleString()} objects</span>
                  )}
                </span>

                <span className="mb-card-created">Created {fmtDate(b.created_at)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
